(() => {
  if (typeof window.FaraAlbumExperience?.prepare === 'function') {
    window.FaraAlbumExperience.prepare();
    return;
  }
  const controllers = new Set();
  const roots = new WeakMap();
  let active;
  const json = value => { try { return JSON.parse(value || '{}'); } catch { return {}; } };
  const numberKey = value => String(Number(value) || value || '');
  const sourceURL = value => {
    if (!value) return '';
    try {
      const url = new URL(value, location.href);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch { return ''; }
  };
  const text = value => typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const selectionIndex = button => Number(button.dataset.albumSelect ?? button.value);
  const motionProfiles = ['broadcast', 'pages', 'standards', 'sonar', 'grain', 'verb', 'redaction', 'sediment', 'portal', 'lineage'];
  const roomQuestion = track => text(track.question) || text(track.note).match(/^.*?\?/s)?.[0] || '';

  class Album {
    constructor(root) {
      this.root = root;
      this.audio = root.querySelector('[data-album-audio]');
      this.toggle = root.querySelector('[data-album-toggle]');
      this.status = root.querySelector('[data-album-status]');
      const payload = json(root.querySelector('[data-album-tracks]')?.textContent);
      this.tracks = Array.isArray(payload.tracks) ? payload.tracks : [];
      this.artMap = payload.art_map || {};
      this.cinemaArt = payload.cinema_art_map || {};
      this.cinema = root.querySelector('[data-album-cinema]');
      const requestedURL = new URL(location.href);
      const requestedTrack = Number(requestedURL.searchParams.get('track'));
      this.index = this.cinema && Number.isInteger(requestedTrack) && requestedTrack >= 1 && requestedTrack <= this.tracks.length ? requestedTrack - 1 : 0;
      this.generation = 0;
      this.returning = 0;
      this.priorRadio = false;
      this.wantsPlay = false;
      this.destroyed = false;
      this.readingFailed = false;
      this.motionPaused = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
      this.wordKey = '';
      this.words = [];
      this.focusedCinema = false;
      this.dock = root.querySelector('[data-album-dock]');
      this.dockDismissed = false;
      this.playerVisible = true;
      this.bindings = [];
      this.lyricLines = [];
      this.audio.preload = 'none';
      this.on(root, 'click', event => {
        const select = event.target.closest('[data-album-select]');
        if (select && root.contains(select)) {
          event.preventDefault();
          this.select(selectionIndex(select), true);
        }
      });
      this.on(this.toggle, 'click', () => {
        if (this.wantsPlay && !this.audio.paused) this.pause(true);
        else this.play();
      });
      this.on(this.audio, 'play', () => {
        if (this.destroyed) { this.audio.pause(); return; }
        if (this.audio.paused) return;
        if (!this.wantsPlay || active !== this) this.claim();
        this.wantsPlay = true;
        this.paint();
      });
      this.on(this.audio, 'playing', () => {
        if (this.wantsPlay) { this.paint(); this.say('playing'); }
      });
      this.on(this.audio, 'pause', () => {
        if (this.audio.ended) return;
        if (this.audio.paused && this.wantsPlay) this.pause(true);
        else this.paint();
      });
      this.on(this.audio, 'ended', () => {
        if (this.cinema && this.wantsPlay && this.index < this.tracks.length - 1) this.select(this.index + 1, true);
        else this.pause(true);
      });
      this.on(this.audio, 'error', () => {
        if (this.audio.getAttribute('src')) { this.pause(true); this.say('error'); }
      });
      this.on(this.audio, 'timeupdate', () => this.updateLyrics());
      this.on(this.audio, 'seeked', () => this.updateLyrics());
      for (const name of ['loadedmetadata', 'durationchange', 'emptied']) this.on(this.audio, name, () => this.updateCinema());
      if (this.cinema) this.bindCinema();
      if (this.dock) this.bindDock();
      for (const detail of root.querySelectorAll('[data-album-lyrics],[data-album-story]')) {
        this.on(detail, 'toggle', () => { if (detail.open) this.enrich(); });
      }
      this.render();
      const requested = requestedURL.searchParams.get('cinema');
      if (this.cinema && ['lyrics', 'story'].includes(requested)) this.openCinema(requested);
    }
    on(target, name, handler) {
      if (!target) return;
      target.addEventListener(name, handler);
      this.bindings.push([target, name, handler]);
    }
    track() { return this.tracks[this.index] || {}; }
    label(name) {
      return (this.root.dataset[`${name}Label`] || '').replace('__title__', text(this.track().title));
    }
    say(name) {
      const value = name ? this.label(name) : '';
      if (this.status) this.status.textContent = value;
      const cinemaStatus = this.cinema?.querySelector('[data-album-cinema-status]');
      if (cinemaStatus) cinemaStatus.textContent = value;
    }
    paint() {
      const playing = Boolean(this.wantsPlay && !this.audio.paused);
      this.root.classList.toggle('is-playing', playing);
      if (this.toggle) {
        const playable = Boolean(sourceURL(this.track().audio_url));
        this.toggle.hidden = !playable;
        this.audio.hidden = !playable || !this.audio.getAttribute('src');
        this.toggle.disabled = !playable;
        this.toggle.setAttribute('aria-pressed', String(playing));
        const label = this.label(playing ? 'pause' : 'play');
        this.toggle.setAttribute('aria-label', [label, text(this.track().title)].filter(Boolean).join(' '));
        const caption = this.toggle.querySelector('[data-album-play-text]');
        const icon = this.toggle.querySelector('[data-album-play-icon]');
        if (caption) caption.textContent = label;
        if (icon) icon.textContent = playing ? 'Ⅱ' : '▶';
      }
      for (const button of this.root.querySelectorAll('[data-album-select]')) {
        const index = selectionIndex(button);
        const selected = index === this.index;
        button.disabled = !sourceURL(this.tracks[index]?.audio_url);
        button.classList.toggle('is-active', selected);
        if (selected) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
        button.setAttribute('aria-pressed', String(selected && playing));
      }
      this.updateCinema();
      this.updateDock();
      const inlineStatus = this.root.querySelector('[data-album-inline-status]');
      if (inlineStatus) inlineStatus.textContent = this.label(playing ? 'playing' : 'listen');
    }
    claim() {
      this.priorRadio = this.priorRadio || Boolean(window.FaraRadio?.isPlaying?.());
      active = this;
      window.dispatchEvent(new CustomEvent('sx:audio-play', {detail: {source: 'fara-album', owner: this.root}}));
      for (const audio of document.querySelectorAll('audio')) if (audio !== this.audio) audio.pause();
    }
    play() {
      const url = sourceURL(this.track().audio_url);
      if (!url || this.destroyed) { this.say('error'); return; }
      const attempt = ++this.generation;
      this.wantsPlay = true;
      this.dockDismissed = false;
      this.claim();
      if (this.audio.getAttribute('src') !== url) this.audio.src = url;
      this.say('loading');
      let promise;
      try { promise = this.audio.play(); }
      catch { this.pause(true); this.say('error'); return; }
      this.paint();
      Promise.resolve(promise).then(() => {
        if (attempt !== this.generation || this.destroyed) return;
        this.paint();
        this.say('playing');
      }).catch(error => {
        if (attempt !== this.generation || this.destroyed) return;
        if (error?.name === 'AbortError' && !this.wantsPlay) return;
        this.pause(true);
        this.say('error');
      });
      this.enrich();
    }
    pause(returnToRadio = false) {
      this.generation++;
      this.wantsPlay = false;
      const shouldReturn = returnToRadio && this.priorRadio;
      this.priorRadio = false;
      this.audio.pause();
      this.paint();
      this.say('paused');
      if (active === this) active = null;
      if (shouldReturn && typeof window.FaraRadio?.resume === 'function') {
        this.returning++;
        let promise;
        try { promise = window.FaraRadio.resume(); }
        catch { this.returning--; return; }
        Promise.resolve(promise).catch(() => {}).finally(() => { this.returning--; });
      }
    }
    external(event) {
      if (event.detail?.source === 'fara-album' && event.detail.owner === this.root) return;
      if (event.detail?.source === 'radio' && this.returning) {
        if (this.wantsPlay) window.FaraRadio?.pause?.();
        return;
      }
      this.pause(false);
      this.say('');
    }
    select(index, shouldPlay = false) {
      if (!Number.isInteger(index) || !this.tracks[index]) return;
      if (shouldPlay && index === this.index && this.wantsPlay && !this.audio.paused) {
        this.pause(true);
        return;
      }
      if (index !== this.index) {
        this.generation++;
        this.wantsPlay = false;
        this.audio.pause();
        this.index = index;
        if (!shouldPlay && this.audio.getAttribute('src')) { this.audio.removeAttribute('src'); this.audio.load(); }
        this.render();
      }
      if (shouldPlay) this.play();
    }
    render() {
      const track = this.track();
      const update = (selector, value) => {
        const element = this.root.querySelector(selector);
        if (element) element.textContent = text(value);
      };
      update('[data-album-track-title]', track.title);
      update('[data-album-track-number]', track.number);
      update('[data-album-scene-caption]', [track.number, track.title].map(text).filter(Boolean).join(' / '));
      update('[data-album-room-progress]', `${track.number} / ${String(this.tracks.length).padStart(2, '0')}`);
      if (this.cinema) {
        const room = Number(track.number);
        const chapter = room === 1 ? 2 : room === 2 ? 1 : room;
        for (const link of this.root.querySelectorAll('[data-album-related-chapter]')) {
          const url = new URL(link.getAttribute('href'), location.href);
          url.searchParams.set('chapter', String(chapter));
          link.setAttribute('href', url.href);
        }
      }
      update('[data-album-track-meta]', [track.credit, track.genre, track.bpm, track.musical_key, track.duration].map(text).filter(Boolean).join(' · '));
      const question = this.cinema ? roomQuestion(track) : '';
      update('[data-album-track-question]', question);
      update('[data-album-track-thesis]', track.thesis || (question && text(track.note).startsWith(question) ? text(track.note).slice(question.length).trim() : ''));
      update('[data-album-track-note]', this.cinema && (question || track.thesis) ? '' : track.note || [track.question, track.thesis].map(text).filter(Boolean).join(' '));
      const image = this.root.querySelector('[data-album-track-art]');
      const art = sourceURL((this.cinema && (this.cinemaArt[track.number] || this.cinemaArt[numberKey(track.number)])) || track.art_url || this.artMap[track.number] || this.artMap[numberKey(track.number)] || this.root.dataset.albumArt);
      if (image && art) {
        if (sourceURL(image.getAttribute('src')) !== art) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = art;
          if (this.cinema && this.hasRendered && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches && !document.hidden) image.animate?.([{opacity:.3,transform:'scale(1.025)'},{opacity:1,transform:'scale(1)'}], {duration:600,easing:'cubic-bezier(.16,1,.3,1)'});
        }
        image.alt = text(track.title);
      }
      this.renderLyrics();
      this.renderStory();
      if (this.cinema) this.renderRooms();
      this.paint();
      this.hasRendered = true;
    }
    renderRooms() {
      for (const button of this.root.querySelector('[data-album-track-list]')?.querySelectorAll('[data-album-select]') || []) {
        const track = this.tracks[selectionIndex(button)];
        const name = button.children[1];
        if (!track || !name) continue;
        let question = name.querySelector('[data-album-row-question]');
        if (roomQuestion(track) && !question) {
          question = document.createElement('small');
          question.setAttribute('data-album-row-question', '');
          name.append(question);
        }
        if (question) question.textContent = roomQuestion(track);
        const meta = button.children[2];
        if (meta) meta.textContent = [track.genre, track.bpm].map(text).filter(Boolean).join(' · ');
      }
    }
    renderLyrics() {
      const detail = this.root.querySelector('[data-album-lyrics]');
      const body = this.root.querySelector('[data-album-lyrics-body]');
      if (!detail || !body) return;
      const cues = Array.isArray(this.track().lyrics) ? this.track().lyrics.filter(cue => Array.isArray(cue) && Number.isFinite(Number(cue[0])) && Number(cue[1]) > Number(cue[0]) && text(cue[2])) : [];
      detail.hidden = !cues.length && (!this.root.dataset.audibleUrl || this.enriched);
      this.lyricLines = cues.map(cue => {
        const element = document.createElement('p');
        element.className = 'fara-album__lyric';
        element.textContent = text(cue[2]);
        return {element, start: Number(cue[0]), end: Number(cue[1])};
      });
      body.replaceChildren(...this.lyricLines.map(line => line.element));
      this.updateLyrics();
    }
    updateLyrics() {
      const time = this.audio.currentTime || 0;
      for (const line of this.lyricLines) {
        const current = time >= line.start && time < line.end;
        line.element.classList.toggle('is-active', current);
        if (current) line.element.setAttribute('aria-current', 'true');
        else line.element.removeAttribute('aria-current');
      }
      const inline = this.root.querySelector('[data-album-inline-line]');
      const inlineStatus = this.root.querySelector('[data-album-inline-status]');
      const current = this.lyricLines.find(line => time >= line.start && time < line.end);
      const upcoming = this.lyricLines.find(line => line.start > time);
      if (inline) inline.textContent = current?.element.textContent || upcoming?.element.textContent || this.lyricLines.at(-1)?.element.textContent || this.label(this.readingFailed ? 'readingError' : 'follow');
      if (inlineStatus) inlineStatus.textContent = this.label(this.wantsPlay && !this.audio.paused ? 'playing' : 'listen');
      this.updateCinema();
      this.updateDock();
    }
    renderStory() {
      const detail = this.root.querySelector('[data-album-story]');
      const body = this.root.querySelector('[data-album-story-body]');
      if (!detail || !body) return;
      const story = this.track().story || {};
      for (const target of [body, this.cinema?.querySelector('[data-album-cinema-story-body]')].filter(Boolean)) {
        const nodes = [];
        const append = (tag, value, className = '') => {
          if (!text(value)) return;
          const element = document.createElement(tag);
          element.className = className;
          element.textContent = text(value);
          nodes.push(element);
        };
        append('h3', story.opening, 'fara-album__story-opening');
        append('p', story.standfirst, 'fara-album__standfirst');
        if (target !== body && this.cinema.open && this.cinema.dataset.mode === 'story' && story.opening) {
          const figure = document.createElement('figure');
          figure.className = 'fara-album__cinema-story-art';
          const image = document.createElement('img');
          image.setAttribute('data-album-story-art', '');
          image.width = 1400; image.height = 788; image.loading = 'lazy'; image.alt = text(this.track().title);
          image.src = sourceURL(this.cinemaArt[this.track().number] || this.cinemaArt[numberKey(this.track().number)] || this.track().art_url || this.root.dataset.albumArt);
          const caption = document.createElement('figcaption');
          caption.textContent = [this.track().number, this.track().title].map(text).filter(Boolean).join(' / ');
          figure.append(image, caption);
          nodes.push(figure);
        }
        for (const section of Array.isArray(story.sections) ? story.sections : []) {
          append('h4', section.heading);
          for (const paragraph of Array.isArray(section.paragraphs) ? section.paragraphs : []) append('p', paragraph);
        }
        append('blockquote', story.wall, 'fara-album__story-wall');
        if (target !== body && !nodes.length) append('p', this.enriched ? this.cinema.dataset.emptyStory : this.label(this.readingFailed ? 'readingError' : 'loading'));
        target.replaceChildren(...nodes);
        if (target === body) detail.hidden = !nodes.length && (!this.root.dataset.audibleUrl || this.enriched);
      }
      const readingTime = this.cinema?.querySelector('[data-album-story-reading-time]');
      if (readingTime) readingTime.textContent = story.readingMinutes ? (this.cinema.dataset.readMinutes || '').replace('__minutes__', text(story.readingMinutes)) : '';
      if (this.storyNumber !== this.track().number) {
        const panel = this.cinema?.querySelector('[data-album-cinema-panel="story"]');
        if (panel) panel.scrollTop = 0;
        this.storyNumber = this.track().number;
      }
    }
    bindCinema() {
      for (const button of this.root.querySelectorAll('[data-album-cinema-open]')) {
        button.setAttribute('aria-haspopup', 'dialog');
        if (this.cinema.id) button.setAttribute('aria-controls', this.cinema.id);
        this.on(button, 'click', () => this.openCinema(button.dataset.albumCinemaOpen));
      }
      this.on(this.cinema.querySelector('[data-album-cinema-close]'), 'click', () => this.closeCinema());
      this.on(this.cinema.querySelector('[data-album-reading-retry]'), 'click', () => this.enrich());
      this.on(this.cinema, 'close', () => { this.updateDock(); if (this.previousFocus?.isConnected) this.previousFocus.focus({preventScroll: true}); });
      this.on(this.cinema.querySelector('[data-album-cinema-motion]'), 'click', () => { this.motionPaused = !this.motionPaused; this.updateCinema(); });
      this.on(this.cinema.querySelector('[data-album-cinema-focus]'), 'click', () => this.setCinemaFocus(!this.focusedCinema));
      this.on(document, 'visibilitychange', () => this.cinema.classList.toggle('is-backgrounded', Boolean(document.hidden)));
      this.on(this.cinema.querySelector('[data-album-cinema-story-next]'), 'click', () => {
        this.select((this.index + 1) % this.tracks.length, this.wantsPlay && !this.audio.paused);
        this.cinema.querySelector('[data-album-cinema-panel="story"]')?.focus({preventScroll: true});
      });
      this.on(this.cinema, 'click', event => {
        const button = event.target.closest('[data-album-cinema-mode]');
        if (button) this.setCinemaMode(button.dataset.albumCinemaMode);
      });
      this.on(this.cinema.querySelector('[data-album-cinema-toggle]'), 'click', () => this.wantsPlay && !this.audio.paused ? this.pause(true) : this.play());
      this.on(this.cinema.querySelector('[data-album-cinema-prev]'), 'click', () => this.moveCinema(-1));
      this.on(this.cinema.querySelector('[data-album-cinema-next]'), 'click', () => this.moveCinema(1));
      this.on(this.cinema.querySelector('[data-album-cinema-seek]'), 'input', event => this.seekCinema(Number(event.target.value)));
      this.on(this.cinema, 'keydown', event => {
        if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
        if (event.key === 'Escape') { event.preventDefault(); this.closeCinema(); return; }
        if (event.target.isContentEditable || event.target.closest?.('button,input,select,textarea,a,[contenteditable]:not([contenteditable="false"]),[role="textbox"]')) return;
        const storyPanel = this.cinema.querySelector('[data-album-cinema-panel="story"]');
        if (storyPanel?.contains(event.target) && [' ', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.key === ' ') { event.preventDefault(); this.wantsPlay && !this.audio.paused ? this.pause(true) : this.play(); }
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.seekCinema(this.audio.currentTime + (event.key === 'ArrowLeft' ? -5 : 5)); }
        else if (event.key.toLowerCase() === 'l') this.setCinemaMode('lyrics');
        else if (event.key.toLowerCase() === 's') this.setCinemaMode('story');
        else if (event.key.toLowerCase() === 'f') { event.preventDefault(); this.setCinemaFocus(!this.focusedCinema); }
        else if (/^[0-9]$/.test(event.key)) { event.preventDefault(); this.select(event.key === '0' ? 9 : Number(event.key) - 1, true); }
      });
    }
    openCinema(mode = 'lyrics') {
      if (!this.cinema || this.destroyed) return;
      this.previousFocus = document.activeElement;
      this.setCinemaMode(mode);
      if (!this.cinema.open) this.cinema.showModal();
      const focus = mode === 'story' ? this.cinema.querySelector('[data-album-cinema-panel="story"]') : this.cinema;
      focus?.focus({preventScroll: true});
      this.updateCinema();
      this.renderStory();
      this.updateDock();
      this.enrich();
    }
    closeCinema() { if (this.cinema?.open) this.cinema.close(); }
    setCinemaFocus(focused) {
      if (!this.cinema) return;
      this.focusedCinema = Boolean(focused);
      this.cinema.classList.toggle('is-focused', this.focusedCinema);
      for (const node of this.cinema.querySelectorAll('[data-album-cinema-secondary]')) node.hidden = this.focusedCinema;
      const button = this.cinema.querySelector('[data-album-cinema-focus]');
      if (button) { button.setAttribute('aria-pressed', String(this.focusedCinema)); button.textContent = this.cinema.dataset[this.focusedCinema ? 'restoreLabel' : 'focusLabel'] || ''; }
    }
    setCinemaMode(mode) {
      if (!this.cinema) return;
      this.cinema.dataset.mode = mode === 'story' ? 'story' : 'lyrics';
      for (const button of this.cinema.querySelectorAll('[data-album-cinema-mode]')) button.setAttribute('aria-pressed', String(button.dataset.albumCinemaMode === this.cinema.dataset.mode));
      for (const panel of this.cinema.querySelectorAll('[data-album-cinema-panel]')) panel.hidden = panel.dataset.albumCinemaPanel !== this.cinema.dataset.mode;
      this.renderStory();
    }
    bindDock() {
      this.on(this.dock.querySelector('[data-album-dock-toggle]'), 'click', () => this.wantsPlay && !this.audio.paused ? this.pause(true) : this.play());
      this.on(this.dock.querySelector('[data-album-dock-prev]'), 'click', () => this.moveCinema(-1));
      this.on(this.dock.querySelector('[data-album-dock-next]'), 'click', () => this.moveCinema(1));
      this.on(this.dock.querySelector('[data-album-dock-seek]'), 'input', event => this.seekCinema(Number(event.target.value)));
      this.on(this.dock.querySelector('[data-album-dock-close]'), 'click', () => { this.dockDismissed = true; this.pause(true); });
      const player = this.root.querySelector('.fara-album__transport');
      if (player && typeof IntersectionObserver === 'function') {
        this.dockObserver = new IntersectionObserver(entries => { this.playerVisible = entries[0].isIntersecting; this.updateDock(); });
        this.dockObserver.observe(player);
      }
    }
    updateDock() {
      if (!this.dock) return;
      this.dock.hidden = !this.audio.getAttribute('src') || this.playerVisible || this.dockDismissed || Boolean(this.cinema?.open);
      const update = (name, value) => { const node = this.dock.querySelector(`[data-album-dock-${name}]`); if (node) node.textContent = text(value); };
      const playing = this.wantsPlay && !this.audio.paused;
      const toggle = this.dock.querySelector('[data-album-dock-toggle]');
      if (toggle) { toggle.textContent = playing ? 'Ⅱ' : '▶'; toggle.setAttribute('aria-pressed', String(Boolean(playing))); toggle.setAttribute('aria-label', `${this.label(playing ? 'pause' : 'play')} ${text(this.track().title)}`); }
      update('title', this.track().title); update('number', this.track().number);
      this.updateTimeline(this.dock, 'dock');
    }
    moveCinema(direction) {
      if (!this.tracks.length) return;
      this.select((this.index + direction + this.tracks.length) % this.tracks.length, true);
    }
    seekCinema(time) {
      if (!this.audio.getAttribute('src') || !Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
      this.audio.currentTime = Math.max(0, Math.min(this.audio.duration, time));
      this.updateLyrics();
    }
    updateCinema() {
      if (!this.cinema || !this.cinema.open) return;
      const track = this.track();
      const update = (selector, value) => { const element = this.cinema.querySelector(selector); if (element) element.textContent = text(value); };
      update('[data-album-cinema-title]', track.title);
      update('[data-album-cinema-number]', track.number);
      update('[data-album-cinema-question]', roomQuestion(track) || track.note);
      const art = sourceURL(this.cinemaArt[track.number] || this.cinemaArt[numberKey(track.number)] || track.art_url || this.root.dataset.albumArt);
      const image = this.cinema.querySelector('[data-album-cinema-art]');
      if (image && art && image.getAttribute('src') !== art) image.src = art;
      const playing = Boolean(this.wantsPlay && !this.audio.paused);
      this.cinema.dataset.motion = motionProfiles[Number(track.number) - 1] || 'broadcast';
      this.cinema.classList.toggle('is-playing', playing);
      this.cinema.classList.toggle('is-motion-paused', this.motionPaused);
      const motion = this.cinema.querySelector('[data-album-cinema-motion]');
      if (motion) { motion.setAttribute('aria-pressed', String(this.motionPaused)); motion.textContent = this.cinema.dataset[this.motionPaused ? 'resumeMotion' : 'pauseMotion'] || ''; }
      const toggle = this.cinema.querySelector('[data-album-cinema-toggle]');
      if (toggle) { toggle.setAttribute('aria-label', `${this.label(playing ? 'pause' : 'play')} ${text(track.title)}`); toggle.setAttribute('aria-pressed', String(playing)); toggle.textContent = playing ? 'Ⅱ' : '▶'; }
      const lines = this.lyricLines;
      const time = this.audio.currentTime || 0;
      const current = lines.findIndex(line => time >= line.start && time < line.end);
      const upcoming = lines.findIndex(line => line.start > time);
      const index = current >= 0 ? current : upcoming === 0 ? 0 : upcoming > 0 ? upcoming - 1 : lines.length - 1;
      this.cinema.dataset.lyricState = current >= 0 ? 'live' : upcoming === 0 ? 'anticipating' : 'holding';
      update('[data-album-cinema-previous]', lines[index - 1]?.element.textContent);
      const value = lines[index]?.element.textContent || (this.enriched ? this.cinema.dataset.emptyLyrics : this.label(this.readingFailed ? 'readingError' : 'loading'));
      const line = this.cinema.querySelector('[data-album-cinema-line]');
      const key = `${track.number}:${index}:${value}`;
      if (line && this.wordKey !== key) {
        this.wordKey = key;
        this.words = String(value || '').trim().split(/\s+/).filter(Boolean).map(word => { const span = document.createElement('span'); span.className = 'fara-album__cinema-word'; span.textContent = word; return span; });
        line.replaceChildren(...this.words.flatMap((word, i) => i ? [document.createTextNode(' '), word] : [word]));
        line.setAttribute('aria-label', text(value));
      }
      const ratio = current >= 0 ? Math.max(0, Math.min(1, (time - lines[current].start) / Math.max(.3, lines[current].end - lines[current].start))) : upcoming === 0 ? 0 : 1;
      this.words.forEach((word, i) => word.classList.toggle('is-sung', i < Math.ceil(ratio * this.words.length)));
      update('[data-album-cinema-following]', lines[index + 1]?.element.textContent);
      this.updateTimeline(this.cinema, 'cinema');
      this.updateDock();
    }
    updateTimeline(container, kind) {
      const time = this.audio.currentTime || 0;
      const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
      const seek = container.querySelector(`[data-album-${kind}-seek]`);
      const clock = value => `${Math.floor(Math.max(0, value) / 60)}:${String(Math.floor(Math.max(0, value) % 60)).padStart(2, '0')}`;
      if (seek) { seek.disabled = !duration || !this.audio.getAttribute('src'); seek.max = String(duration); seek.value = String(time); seek.setAttribute('aria-valuetext', `${clock(time)} / ${clock(duration)}`); }
      const node = container.querySelector(`[data-album-${kind}-time]`);
      if (node) node.textContent = `${clock(time)} / ${clock(duration)}`;
    }
    renderReadingState() {
      const notice = this.cinema?.querySelector('[data-album-reading-error]');
      if (notice) notice.hidden = !this.readingFailed;
    }
    enrich() {
      const url = sourceURL(this.root.dataset.audibleUrl);
      if (!url || this.enriched || this.enriching || this.destroyed) return;
      this.readingFailed = false;
      this.renderReadingState();
      this.renderStory();
      this.updateCinema();
      this.enriching = fetch(url, {credentials: 'same-origin'}).then(response => {
        if (!response.ok) throw new Error();
        return response.json();
      }).then(payload => {
        if (this.destroyed) return;
        const rich = new Map((Array.isArray(payload.tracks) ? payload.tracks : []).map(track => [numberKey(track.number), track]));
        this.tracks = this.tracks.map(track => ({...track, ...rich.get(numberKey(track.number)), art_url: track.art_url || this.artMap[track.number] || this.artMap[numberKey(track.number)]}));
        this.enriched = true;
        this.render();
        this.say(this.wantsPlay && !this.audio.paused ? 'playing' : '');
      }).catch(() => {
        if (this.destroyed) return;
        this.readingFailed = true;
        this.say('readingError');
        this.renderStory();
        this.updateCinema();
        this.renderReadingState();
      }).finally(() => { this.enriching = null; });
    }
    destroy() {
      this.closeCinema();
      this.dockObserver?.disconnect();
      this.pause(true);
      this.destroyed = true;
      for (const [target, name, handler] of this.bindings) target.removeEventListener(name, handler);
      this.audio.removeAttribute('src');
      this.audio.load();
      controllers.delete(this);
    }
  }
  const prepare = () => {
    for (const controller of controllers) if (!controller.root.isConnected) controller.destroy();
    for (const root of document.querySelectorAll('[data-fara-album]')) {
      if (roots.has(root) || !root.querySelector('[data-album-audio]')) continue;
      const controller = new Album(root);
      roots.set(root, controller);
      controllers.add(controller);
    }
  };
  window.addEventListener('sx:audio-play', event => { for (const controller of controllers) controller.external(event); });
  window.addEventListener('fara:before-navigate', () => { for (const controller of controllers) { controller.closeCinema(); controller.pause(true); } });
  window.addEventListener('pagehide', () => { for (const controller of controllers) controller.pause(false); });
  document.addEventListener('shopify:section:unload', () => { for (const controller of controllers) controller.pause(true); });
  document.addEventListener('shopify:section:load', prepare);
  window.FaraAlbumExperience = {prepare};
  prepare();
})();
